import React, { useContext, useCallback, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { assets, cssGenerator } from '@yola/ws-sdk';
import useFeatureFlags from 'yola-editor/src/js/modules/feature-flags/hooks/use-feature-flags';
import { designSystem } from '@yola/ws-ui';
import elementSettings from 'src/js/modules/element-settings';
import ImagePreview from '../components/image-preview';
import useOnceWhen from 'src/js/modules/utils/custom-hooks/use-once-when';
import chunkIds from '../constants/chunk-ids';
import fallbackValues from '../constants/fallback-values';
import { imageFieldNames } from '../constants/field-names';
import settingIds from '../constants/setting-ids';
import AltControl from '../components/alt-control';
import LinkSettingsContainer from './link-settings-container';
import createSubmitActionsImageHandler from '../helpers/create-submit-actions-image-handler';
import getEffectiveResponsiveClassValue from 'src/js/modules/extensions/common/helpers/get-effective-responsive-class-value';
import getEffectiveResponsiveAttribute from 'src/js/modules/extensions/common/helpers/get-effective-responsive-attribute';
import getBreakpointForCurrentDevice from 'src/js/modules/breakpoint-editing/helpers/get-breakpoint-for-current-device';
import { DESKTOP_BREAKPOINT } from 'src/js/modules/breakpoint-editing/constants/breakpoints';
import ImageActionsContainer from './image-actions-container';

const { Box, Divider } = designSystem;
const {
  Context: ElementSettingsContext,
  constants: { stages },
  helpers: { wrapSharedDataWithId, getSharedDataById },
  hooks: { useMountChunk },
} = elementSettings;
const { CONTENT_SCALE } = cssGenerator.constants.attributes;
const { CONTENT_POSITION } = cssGenerator.constants.classNames;

const getDefaultValue = ({
  mediaContainerElement,
  imageElement,
  cachedFieldsValues,
  activeBreakpoint,
}) => {
  const src =
    cachedFieldsValues?.[imageFieldNames.IMAGE_SRC] ??
    (!mediaContainerElement.hasAttribute('placeholder') && imageElement?.getAttribute('src')) ??
    fallbackValues.IMAGE_SRC;
  const position =
    cachedFieldsValues?.[imageFieldNames.IMAGE_POSITION] ??
    getEffectiveResponsiveClassValue({
      element: mediaContainerElement,
      className: CONTENT_POSITION,
      breakpoint: activeBreakpoint,
    }) ??
    undefined;
  const scale =
    cachedFieldsValues?.[imageFieldNames.IMAGE_SCALE] ??
    getEffectiveResponsiveAttribute({
      element: mediaContainerElement,
      attr: CONTENT_SCALE,
      breakpoint: activeBreakpoint,
    }) ??
    undefined;
  const alt =
    cachedFieldsValues?.[imageFieldNames.IMAGE_ALT] ?? imageElement?.getAttribute('alt') ?? '';

  return {
    [imageFieldNames.IMAGE_SRC]: src,
    ...(position !== undefined && { [imageFieldNames.IMAGE_POSITION]: position }),
    ...(scale !== undefined && { [imageFieldNames.IMAGE_SCALE]: scale }),
    [imageFieldNames.IMAGE_ALT]: alt,
  };
};

function ImageSettingsContainer({ settingId, tabId, mediaContainerElement, cachedFieldsValues }) {
  const {
    tabs,
    form: { control, setValue },
    callbacks,
    sharedData,
  } = useContext(ElementSettingsContext);

  const [featureFlags] = useFeatureFlags(['breakpoint_editing']);
  const { breakpoint_editing: isBreakpointEditingEnabled } = featureFlags;

  const activeBreakpoint = isBreakpointEditingEnabled
    ? getBreakpointForCurrentDevice()
    : DESKTOP_BREAKPOINT;

  const imageElement = mediaContainerElement.querySelector('img');

  const chunkId = chunkIds.IMAGE;
  const isActiveTab = tabs.activeId === tabId;

  const imageElementRef = useRef(imageElement);
  const defaultValues = useMemo(
    () =>
      getDefaultValue({
        mediaContainerElement,
        imageElement,
        cachedFieldsValues,
        activeBreakpoint,
      }),
    [cachedFieldsValues, imageElement, mediaContainerElement, activeBreakpoint]
  );
  const imageSettingsSharedData = getSharedDataById(chunkId, sharedData.internal);

  const rawImageSrc = imageElementRef.current?.getAttribute('src') || '';
  const imageSrc =
    rawImageSrc && !mediaContainerElement.hasAttribute('placeholder')
      ? assets.helpers.addBaseHref(rawImageSrc)
      : '';

  const handlePersistSharedData = useCallback(
    (data) => {
      sharedData.onPersist(
        wrapSharedDataWithId(chunkId, {
          ...getSharedDataById(chunkId, sharedData.internal),
          ...data,
        })
      );
    },
    [sharedData, chunkId]
  );

  const handleAltChange = useCallback(() => {
    // Add logic here
  }, []);

  const handleAltInputClick = useCallback(() => {
    callbacks.onInputClick({
      settingId: settingIds.THIS_MEDIA,
      controlId: 'alt-title',
    });
  }, [callbacks]);

  const handleImageCrop = useCallback(
    (changedProps) => {
      const dataToPersist = changedProps.reduce((acc, [prop, value]) => {
        if (prop === CONTENT_POSITION) {
          acc[imageFieldNames.IMAGE_POSITION] = value;
        }

        if (prop === CONTENT_SCALE) {
          acc[imageFieldNames.IMAGE_SCALE] = value;
        }

        return acc;
      }, {});

      handlePersistSharedData(dataToPersist);
    },
    [handlePersistSharedData]
  );

  const handleImageReplace = useCallback(
    ({ source }) => {
      const dataToPersist = {
        [imageFieldNames.IMAGE_SRC]: source,
        [imageFieldNames.IMAGE_POSITION]: fallbackValues.IMAGE_POSITION,
        [imageFieldNames.IMAGE_SCALE]: fallbackValues.IMAGE_SCALE,
      };

      handlePersistSharedData(dataToPersist);
    },
    [handlePersistSharedData]
  );

  const actionsHandler = useMemo(
    () => createSubmitActionsImageHandler({ mediaContainerElement, breakpoint: activeBreakpoint }),
    [mediaContainerElement, activeBreakpoint]
  );

  useOnceWhen(!!imageSettingsSharedData, () => {
    // Should be set in the next animation frame to avoid race condition with the setup of the form values
    requestAnimationFrame(() => {
      Object.entries(imageSettingsSharedData).forEach(([fieldName, value]) => {
        setValue(fieldName, value, { shouldDirty: true });
      });
    });
  });

  // Within image settings, multiple chunks are initialized (IMAGE, LINK, and maybe others in the future),
  // but we cannot allow each chunk to have its own actions handler as this would
  // cause conflicts since they all need to build the same markup structure.
  // Therefore, we use only one actions handler in the main chunk (IMAGE).
  // To do this, we need to collect field values from all related chunks.
  const { isChunkMounted } = useMountChunk(chunkId, {
    tabId,
    defaultValues,
    stageHandlers: [
      {
        stage: stages.SUBMIT_ACTIONS_GENERATION,
        handler: actionsHandler,
      },
    ],
    withUnmount: true,
  });

  if (!isChunkMounted) return null;

  return (
    <>
      <Box paddingLeft="spacing-m" paddingRight="spacing-m" paddingBottom="spacing-xs">
        <div className="ws-media-preview-with-actions">
          <ImagePreview
            url={imageSrc}
            mediaContainerElement={mediaContainerElement}
            previewKey={isActiveTab}
          />
          <div className="ws-media-preview-with-actions__actions">
            <ImageActionsContainer
              mediaContainerElement={mediaContainerElement}
              onCrop={handleImageCrop}
              onReplace={handleImageReplace}
            />
          </div>
        </div>
      </Box>
      <Divider appearance="light-grey" />
      <Box
        paddingTop="spacing-xs"
        paddingBottom="spacing-xs"
        paddingLeft="spacing-m"
        paddingRight="spacing-m"
      >
        <AltControl
          control={control}
          onChange={handleAltChange}
          onInputClick={handleAltInputClick}
        />
      </Box>
      <Divider appearance="light-grey" />
      <Box
        paddingTop="spacing-xs"
        paddingBottom="spacing-xs"
        paddingLeft="spacing-m"
        paddingRight="spacing-m"
      >
        <LinkSettingsContainer
          settingId={settingId}
          tabId={tabId}
          mediaContainerElement={mediaContainerElement}
          cachedFieldsValues={cachedFieldsValues}
        />
      </Box>
    </>
  );
}

ImageSettingsContainer.propTypes = {
  settingId: PropTypes.string.isRequired,
  tabId: PropTypes.string.isRequired,
  mediaContainerElement: PropTypes.oneOfType([PropTypes.instanceOf(Element), PropTypes.object])
    .isRequired,
  cachedFieldsValues: PropTypes.object,
};

export default ImageSettingsContainer;
